import { useEffect, useMemo, useReducer } from 'react';
import produce from 'immer';
import axios from 'axios';
import ProxyLayer from 'base/api/ProxyLayer';
import { FieldSoilRequest, SoilTypeResponse } from 'base/types/SoilType';

interface UseFieldSoilsOptions {
  fields: FieldSoilRequest[];
  cropRegion?: string;
}

interface SoilState {
  key: string;
  data: SoilTypeResponse | null;
  status: 'idle' | 'loading' | 'success' | 'error';
}

interface UseFieldSoilsReturn {
  data: SoilTypeResponse | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
}

export default function useFieldSoils({
  fields,
  cropRegion,
}: UseFieldSoilsOptions): UseFieldSoilsReturn {
  const key = JSON.stringify([fields, cropRegion]);
  const [attempt, retry] = useReducer((value: number) => value + 1, 0);
  const [state, update] = useReducer(
    produce((draft: SoilState, next: SoilState) => {
      Object.assign(draft, next);
    }),
    { key: '', data: null, status: 'idle' }
  );
  const params = useMemo(
    () => ({
      fields: fields.map((field, index) => ({
        id: index + 1,
        acres: field.acres,
        geometry: field.geometry,
      })),
      cropRegion,
      brandName: 'SYNGENTA',
    }),
    [fields, cropRegion]
  );

  useEffect(() => {
    if (!params.fields.length) {
      return;
    }
    const source = ProxyLayer.cancelToken();
    let active = true;
    update({ key, data: null, status: 'loading' });
    // The form action caches unrelated field IDs in localStorage. Keep report
    // requests scoped to this geometry and cancel them when the report changes.
    ProxyLayer.getFieldSoils({ params, cancelToken: source.token })
      .then((data) => {
        if (active) {
          const soilGrid = Object.fromEntries(
            fields.map((field, index) => [field.id, data.soilGrid[index + 1] ?? []])
          );
          update({ key, data: { ...data, soilGrid }, status: 'success' });
        }
      })
      .catch((error: unknown) => {
        if (active && !axios.isCancel(error)) {
          update({ key, data: null, status: 'error' });
        }
      });
    return () => {
      active = false;
      source.cancel();
    };
  }, [params, fields, key, attempt]);

  return {
    data: state.key === key ? state.data : null,
    loading: fields.length > 0 && (state.key !== key || state.status === 'loading'),
    error: state.key === key && state.status === 'error',
    retry,
  };
}
